import React, { FC } from 'react';
import PostComposerContentText from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentText';
import { PostComposerContentProps } from 'app/modules/posts/modules/post/components/composer/PostComposerContent';
import PostComposerContentMediaAttachments from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentMediaAttachemnts';
import { PseudoPostType } from 'constants/post';
import PostComposerContentLinkAttachments from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentLinkAttachments';
import PostsConfig from 'app/config/posts';
import PostComposerContentFirstComment from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentFirstComment';
import PostComposerContentFacebookVideoTitle from './postComposerContentFacebook/PostComposerContentFacebookVideoTitle';
import PostComposerContentFacebookVideoThumbnail from './postComposerContentFacebook/PostComposerContentFacebookVideoThumbnail';
import PostComposerContentLinkAppender from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentLinkAppender';
import { MediaLibraryItemFileType } from 'types/MediaLibrary';
import { TYPE_VIDEO } from '@kontentino/kontentino-constants/Posts';
import { VIDEO_STATUS_AVAILABLE } from '@kontentino/kontentino-constants/Video';

type Props = PostComposerContentProps;

const PostComposerContentFacebook: FC<Props> = ({
  value,
  onChange,
  options,
  onOptionsChange,
  onTextEditorInit,
  onTranslationEditorInit,
  swapTextTranslation,
}) => {
  const pseudoType = value.pseudoType;

  const getAllowedFiles = (): MediaLibraryItemFileType[] => {
    if ([PseudoPostType.Video, PseudoPostType.Reel].includes(pseudoType)) {
      return ['video'];
    }

    if (PseudoPostType.Story === pseudoType) {
      return ['image', 'video'];
    }

    return ['image'];
  };

  const canSetupReelVideoThumbnail =
    pseudoType === PseudoPostType.Reel &&
    value.attachments.cards.length === 1 &&
    value.type === TYPE_VIDEO &&
    value.attachments.cards[0]?.medium?.type === 'video' &&
    value.attachments.cards[0]?.medium?.status === VIDEO_STATUS_AVAILABLE;

  return (
    <>
      <PostComposerContentText
        value={value}
        onChange={onChange}
        onTextEditorInit={onTextEditorInit}
        onTranslationEditorInit={onTranslationEditorInit}
        swapTextTranslation={swapTextTranslation}
        disabled={pseudoType === PseudoPostType.Story}
      />
      {![PseudoPostType.Story, PseudoPostType.Reel].includes(pseudoType) && (
        <PostComposerContentFirstComment onChange={onChange} value={value} />
      )}
      {pseudoType && (
        <>
          {[
            PseudoPostType.Video,
            PseudoPostType.PhotoGallery,
            PseudoPostType.Story,
            PseudoPostType.Reel,
          ].includes(pseudoType) && (
            <PostComposerContentMediaAttachments
              value={value}
              onChange={onChange}
              allowedFiles={getAllowedFiles()}
              canSetupVideoThumbnail={canSetupReelVideoThumbnail}
              maxCount={
                [PseudoPostType.Video, PseudoPostType.Reel].includes(pseudoType)
                  ? PostsConfig.MIN_FILES_TO_ACCEPT
                  : PostsConfig.MAX_FILES_TO_ACCEPT
              }
            />
          )}
          {PseudoPostType.Video === pseudoType &&
            value.attachments.cards.length > 0 && (
              <>
                <PostComposerContentFacebookVideoTitle
                  value={value}
                  onChange={onChange}
                />
                <PostComposerContentFacebookVideoThumbnail value={value} />
              </>
            )}
          {[PseudoPostType.Link, PseudoPostType.Carousel].includes(
            pseudoType,
          ) && (
            <PostComposerContentLinkAttachments
              isFullLinkEditable
              withLinkOwnership={PseudoPostType.Link === pseudoType}
              withMultiShareEndCard={PseudoPostType.Carousel === pseudoType}
              withDescriptionField
              withCallToAction
              value={value}
              onChange={onChange}
              onOptionsChange={onOptionsChange}
              maxCount={
                pseudoType === PseudoPostType.Link
                  ? PostsConfig.MIN_FILES_TO_ACCEPT
                  : PostsConfig.MAX_FILES_TO_ACCEPT
              }
            />
          )}
          {options.canAppendLinkFromText &&
            [PseudoPostType.Link, PseudoPostType.Carousel].includes(
              pseudoType,
            ) && (
              <PostComposerContentLinkAppender
                value={value}
                onChange={onChange}
              />
            )}
        </>
      )}
    </>
  );
};

export default PostComposerContentFacebook;
